import { useUserBookings } from "@/hooks/use-user-bookings";
import { dateToLocalTime, formatInTz } from "@/utils/format-date-with-locale";
import { hexToRgbString } from "@/utils/hexToRgbString";
import { Card, Chip } from "@heroui/react";
import { EventStatus } from "@/types/EventStatus";
import { IconCircleCheck, IconUser, IconUsers } from "@tabler/icons-react";

export const TimeSlot = ({
  event,
  onEventClick,
  customEventRender,
  t,
  timezone,
}: {
  event: any;
  onEventClick: (event: any) => void;
  customEventRender?: (event: any) => React.ReactNode;
  t: any; // Replace with your translation function
  timezone: string;
}) => {
  const { hasBookedEvent } = useUserBookings();

  if (!event) return null;

  // If custom render is provided, use it
  if (customEventRender) {
    return customEventRender(event);
  }

  const startValue = event.startAt ?? event.startTime;
  const endValue = event.endAt ?? event.endTime;
  if (!startValue) return null;

  const startTime = dateToLocalTime(new Date(startValue), timezone);
  const endTime = endValue
    ? dateToLocalTime(new Date(endValue), timezone)
    : null;
  const duration =
    typeof event.durationMin === "number"
      ? event.durationMin
      : endTime
        ? Math.round((endTime.getTime() - startTime.getTime()) / (1000 * 60))
        : 0;

  const isUserBooked = hasBookedEvent(event.id);
  const isCanceled =
    event.isCancelled ||
    event.status === EventStatus.CANCELED ||
    event.status === "CANCELLED";

  const confirmedBookings = event.bookings?.confirmed;
  const spotsLeft =
    typeof confirmedBookings === "number" && typeof event.capacity === "number"
      ? event.capacity - confirmedBookings
      : null;
  const isFullyBooked = spotsLeft !== null && spotsLeft <= 0;
  const isDisabled =
    isCanceled ||
    event.status === EventStatus.COMPLETED ||
    event.status === EventStatus.HOLIDAY;

  const isWorkshopEvent = event.eventType === "WORKSHOP";

  return (
    <Card
      className={`p-2 w-full rounded shadow-sm bg-white border-l-4 flex flex-col gap-1 hover:scale-105 ${
        isCanceled ||
        event.status === EventStatus.COMPLETED ||
        event.status === EventStatus.HOLIDAY
          ? "opacity-50 border-gray-300"
          : event.status === "IN_PROGRESS"
            ? "border-yellow-500"
            : "border-blue-500"
      }`}
      isPressable
      onPress={() => {
        if (!isDisabled) {
          onEventClick(event);
        }
      }}
      disableRipple
      style={{
        borderLeftColor: event.color || event.class?.color || "blue",
        backgroundColor:
          event.color || event.class?.color
            ? `rgba(${hexToRgbString(event.color || event.class?.color)}, 0.1)`
            : "rgba(0, 0, 255, 0.1)",
      }}
    >
      {/* Time and duration */}
      <div className="flex items-center justify-between mb-1">
        <div className="text-sm font-medium text-gray-700">
          {formatInTz(startTime, "HH:mm", timezone)}
          <span className="mx-1">•</span>
          <span className="text-gray-600">{duration} mins</span>
        </div>
      </div>

      {isWorkshopEvent && (
        <Chip color="primary" variant="flat" size="sm">
          {t("workshop")}
        </Chip>
      )}

      {/* Class name */}
      <div className="font-semibold text-left text-gray-900 mb-1">
        {event.title || event.class?.name || "Class Name"}
      </div>

      {/* Teacher info */}
      <div className="flex items-center gap-1.5 text-sm text-gray-600 truncate">
        <IconUser size={14} strokeWidth={2.5} />
        <span>
          {event.teacherName || event.teacher?.user?.name || "Teacher"}
        </span>
      </div>

      {/* Level info */}
      {/* <div className="flex items-center gap-1.5 text-sm text-gray-600">
        <IconChartBarPopular size={14} strokeWidth={2.5} />
        <span>
          {t(`${event.level}`) || event.level?.toLowerCase().replace("_", " ")}
        </span>
      </div> */}

      {/* <div className="flex items-center gap-1.5 text-sm text-gray-600">
        <IconUsers size={14} strokeWidth={2.5} />
        <span>
          {event.bookings.confirmed} / {event.capacity}
        </span>
      </div> */}

      {/* Studio info */}
      {/* <div className="flex items-center gap-1.5 text-sm text-gray-600">
        <IconMapPin size={14} strokeWidth={2.5} />
        <span className=" truncate">
          {event.room || event.studio?.name || ""}
        </span>
      </div> */}

      {/* Spots info */}
      {spotsLeft !== null && spotsLeft < 5 && (
        <div className="flex items-center gap-1.5 text-sm text-gray-600">
          <IconUsers size={14} strokeWidth={2.5} />
          <span>
            {isFullyBooked ? (
              <span className="text-danger">{t("fullyBooked")}</span>
            ) : (
              t("lastSpots")
            )}
          </span>
        </div>
      )}

      {/* User booking confirmation */}
      {isUserBooked && (
        <div className="flex items-center text-left justify-start gap-1.5 text-sm text-success-500 font-medium mt-2 border-t border-gray-200 pt-1.5">
          <IconCircleCheck size={16} />
          <span>{t("yourein")}</span>
        </div>
      )}
    </Card>
  );
};
